import { useEffect, useRef } from "react";
import { containRect } from "../../utils/videoContainCoords";
import { normalizeTableCorners } from "../../utils/tableCorners";
import { subscribePresentedVideoTime } from "../../utils/videoFrameDuration";
import "./poseOverlays.css";

const BALL_COLOR = "#FFEB3B";
const DOT_COLOR = "rgba(255, 235, 59, 0.92)";
const CURVE_COLOR = "rgba(255, 235, 59, 0.38)";
const CURVE_PLAYED_COLOR = "rgba(255, 235, 59, 0.92)";
const ROI_COLOR = "#FBBF24";
/** Last N real detections drawn as dots near the playhead. */
const LAST_DOTS = 8;
/** Only keep detection dots inside this window of the playhead. */
const DOT_WINDOW_MS = 500;
/** Show event markers within this window of the playhead. */
const EVENT_WINDOW_MS = 400;

const EVENT_STYLES = {
  TABLE_BOUNCE: { color: "#4FC3F7", label: "B" },
  BEGIN_STROKE: { color: "#FF8A65", label: "S" },
  OUT_OF_TABLE: { color: "#EF5350", label: "O" },
  NEAR_NET: { color: "#CE93D8", label: "N" },
  NET_CROSS: { color: "#BA68C8", label: "X" },
};
const SERVE_BOUNCE_COLOR = "#4FC3F7";
const SERVE_P1_COLOR = "#A5D6A7";
const SERVE_SHORT_COLOR = "#4FC3F7";
const SERVE_LONG_COLOR = "#FFB74D";
const SERVE_MISSED_COLOR = "#EF5350";
const MAX_SERVE_FAR_BOUNCES = 2;

function lengthColor(length) {
  if (length === "short") return SERVE_SHORT_COLOR;
  if (length === "long") return SERVE_LONG_COLOR;
  if (length === "missed") return SERVE_MISSED_COLOR;
  return SERVE_BOUNCE_COLOR;
}

/**
 * Spectator net X in normalized image coords (mid of the four table corners).
 */
export function tableNetXNorm(corners) {
  const pts = normalizeTableCorners(corners);
  if (!pts) return 0.5;
  const xs = pts.map((p) => Number(p.x)).filter((x) => Number.isFinite(x));
  if (xs.length < 4) return 0.5;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function servePrepStartMs(event) {
  const t = Number(
    event?.t_prep_start_ms ??
      event?.phases?.preparation?.t_start_ms ??
      event?.t_steady_ms ??
      event?.t_peak_ms ??
      event?.t_ms
  );
  return Number.isFinite(t) ? t : null;
}

function bounceOnOppositeSide(bounce, serverSide, netX) {
  const x = Number(bounce?.x);
  if (!Number.isFinite(x)) return false;
  const mid = Number.isFinite(netX) ? netX : 0.5;
  if (serverSide === "right") return x < mid - 0.008;
  return x > mid + 0.008;
}

/**
 * 1st and 2nd table bounces on the receiver side of the current serve.
 * Marks appear as they happen and flush when the next serve prep starts.
 */
export function serveBounceMarksAt(
  serveEvents,
  bounceEvents,
  tMs,
  tableCorners = null
) {
  const serves = (Array.isArray(serveEvents) ? serveEvents : [])
    .map((event) => ({ event, t0: servePrepStartMs(event) }))
    .filter((s) => s.t0 != null)
    .sort((a, b) => a.t0 - b.t0);
  if (!serves.length) return [];
  let idx = -1;
  for (let i = 0; i < serves.length; i += 1) {
    if (serves[i].t0 <= tMs) idx = i;
    else break;
  }
  if (idx < 0) return [];
  const current = serves[idx];
  const labeled = [];
  const pushPt = (pt, n, color) => {
    if (!pt || !Number.isFinite(Number(pt.x)) || !Number.isFinite(Number(pt.y))) return;
    const t = Number(pt.t_ms);
    if (!Number.isFinite(t) || t > tMs) return;
    labeled.push({
      x: Number(pt.x),
      y: Number(pt.y),
      t_ms: t,
      n,
      color,
    });
  };
  if (current.event?.p1 || current.event?.p2 || current.event?.length) {
    const col = lengthColor(current.event.length);
    pushPt(current.event.p1, 1, SERVE_P1_COLOR);
    pushPt(current.event.p2, 2, col);
    pushPt(current.event.p3, 3, col);
    if (labeled.length) return labeled;
  }
  const nextT0 = idx + 1 < serves.length ? serves[idx + 1].t0 : Infinity;
  const netX = tableNetXNorm(tableCorners);
  const serverSide =
    current.event.server_side === "right" || current.event.player === "B"
      ? "right"
      : "left";
  const flightStart = Number(
    current.event.phases?.contact?.t_start_ms ??
      current.event.t_peak_ms ??
      current.event.t_ms ??
      current.t0
  );
  const bounces = (Array.isArray(bounceEvents) ? bounceEvents : [])
    .filter((ev) => ev && ev.type === "TABLE_BOUNCE")
    .filter((ev) => {
      const t = Number(ev.t_ms);
      return Number.isFinite(t) && t >= flightStart && t < nextT0 && t <= tMs;
    })
    .filter((ev) => bounceOnOppositeSide(ev, serverSide, netX))
    .sort((a, b) => Number(a.t_ms) - Number(b.t_ms));
  return bounces.slice(0, MAX_SERVE_FAR_BOUNCES).map((ev, i) => ({
    x: Number(ev.x),
    y: Number(ev.y),
    t_ms: Number(ev.t_ms),
    n: i + 1,
  }));
}

/**
 * Sample at or before tMs (assumes samples sorted by t_ms ascending).
 * Preferring past frames avoids jumping ahead of the playhead.
 */
export function ballSampleAt(samples, tMs) {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  let lo = 0;
  let hi = samples.length - 1;
  let best = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = samples[mid].t_ms || 0;
    if (t <= tMs) {
      best = samples[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (best) return best;
  // Before first sample — use first if within a small window.
  const first = samples[0];
  if ((first.t_ms || 0) - tMs <= 40) return first;
  return null;
}

/**
 * State label at or before tMs.
 */
export function ballStateAt(states, tMs) {
  if (!Array.isArray(states) || states.length === 0) return null;
  let best = null;
  for (let i = 0; i < states.length; i++) {
    const s = states[i];
    if ((s.t_ms || 0) <= tMs) best = s.state || null;
    else break;
  }
  return best;
}

/**
 * Last N detected samples near the playhead. Does not pin early-clip hits
 * on screen for the rest of playback.
 */
export function hitsAroundPlayhead(
  samples,
  tMs,
  maxN = LAST_DOTS,
  windowMs = DOT_WINDOW_MS
) {
  if (!Array.isArray(samples) || samples.length === 0 || maxN <= 0) return [];
  const near = [];
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i];
    if (!s?.detected || s.source === "physics") continue;
    const t = Number(s.t_ms) || 0;
    if (t > tMs + 40) continue;
    if (tMs - t > windowMs) continue;
    near.push(s);
  }
  if (near.length <= maxN) return near;
  return near.slice(near.length - maxN);
}

function rectValid(r) {
  return (
    r &&
    Number.isFinite(Number(r.x)) &&
    Number.isFinite(Number(r.y)) &&
    Number(r.w) >= 0.02 &&
    Number(r.h) >= 0.02
  );
}

function drawDetectionRoiBox(ctx, roi, rect, label) {
  if (!rectValid(roi) || !rect) return;
  const { x: ox, y: oy, w, h } = rect;
  const x = ox + Number(roi.x) * w;
  const y = oy + Number(roi.y) * h;
  const bw = Number(roi.w) * w;
  const bh = Number(roi.h) * h;
  const strokeW = Math.max(1.75, Math.min(w, h) * 0.0034);
  ctx.save();
  ctx.fillStyle = `${ROI_COLOR}14`;
  ctx.strokeStyle = ROI_COLOR;
  ctx.lineWidth = strokeW;
  ctx.setLineDash([strokeW * 4.5, strokeW * 2.4]);
  ctx.fillRect(x, y, bw, bh);
  ctx.strokeRect(x, y, bw, bh);
  ctx.setLineDash([]);
  if (label) {
    const fontSize = Math.max(11, Math.min(w, h) * 0.026);
    ctx.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillStyle = ROI_COLOR;
    ctx.textBaseline = "bottom";
    const ty = Math.max(oy + fontSize + 2, y - 4);
    ctx.fillText(label, x + strokeW, ty);
  }
  ctx.restore();
}

function eventsNearPlayhead(events, tMs, windowMs = EVENT_WINDOW_MS) {
  if (!Array.isArray(events) || events.length === 0) return [];
  return events.filter((e) => {
    const t = e.t_ms || 0;
    return t <= tMs + 40 && tMs - t <= windowMs;
  });
}

export function trajectoryAt(trajectories, tMs) {
  if (!Array.isArray(trajectories) || trajectories.length === 0) return null;
  let active = null;
  let nearest = null;
  let nearestDt = Infinity;
  for (let i = 0; i < trajectories.length; i++) {
    const tr = trajectories[i];
    const t0 = Number(tr?.t0_ms) || 0;
    const t1 = Number(tr?.t1_ms) || 0;
    if (tMs >= t0 && tMs <= t1) {
      active = tr;
      break;
    }
    const dt = tMs < t0 ? t0 - tMs : tMs - t1;
    if (dt < nearestDt) {
      nearestDt = dt;
      nearest = tr;
    }
  }
  return active || (nearestDt < 250 ? nearest : null);
}

function polylineSpeedAt(poly, tMs) {
  if (!Array.isArray(poly) || poly.length === 0) return null;
  let best = null;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    if ((p.t_ms || 0) <= tMs) best = p;
    else break;
  }
  const spd = Number(best?.speed_kmh);
  return Number.isFinite(spd) && spd > 0 ? spd : null;
}

function drawFittedCurve(ctx, poly, tMs, ox, oy, w, h) {
  if (!Array.isArray(poly) || poly.length < 2) return;
  const toXy = (p) => [ox + Number(p.x) * w, oy + Number(p.y) * h];
  ctx.save();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = CURVE_COLOR;
  ctx.lineWidth = Math.max(1.6, Math.min(w, h) * 0.0032);
  ctx.beginPath();
  poly.forEach((p, i) => {
    const [x, y] = toXy(p);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  const played = poly.filter((p) => (p.t_ms || 0) <= tMs + 12);
  if (played.length >= 2) {
    ctx.strokeStyle = CURVE_PLAYED_COLOR;
    ctx.lineWidth = Math.max(2.2, Math.min(w, h) * 0.0044);
    ctx.beginPath();
    played.forEach((p, i) => {
      const [x, y] = toXy(p);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Canvas ball trajectory overlay (normalized 0–1 coords), same contain layout as pose.
 */
export function BallTrackOverlay({
  samples,
  events = null,
  trajectories = null,
  tMs = 0,
  /** HTMLVideoElement ref — sync ball track to presented frames (source fps). */
  videoRef = null,
  videoWidth,
  videoHeight,
  visible = true,
  detectionRoi = null,
  detector = null,
  serveEvents = null,
  tableCorners = null,
}) {
  const canvasRef = useRef(null);
  const tMsRef = useRef(tMs);
  tMsRef.current = tMs;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !visible) return undefined;

    const paintAt = (playheadMs) => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const cssW = parent.clientWidth || 1;
      const cssH = parent.clientHeight || 1;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(cssW * dpr));
      canvas.height = Math.max(1, Math.floor(cssH * dpr));
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const rect = containRect(cssW, cssH, videoWidth, videoHeight);
      const { x: ox, y: oy, w, h } = rect;
      const det = String(detector || "").toLowerCase();
      const detLabel =
        det === "fmo"
          ? "FMO ROI"
          : det === "tracknet"
            ? "TrackNet ROI"
            : det === "yolov5"
              ? "YOLOv5 ROI"
              : det === "yolo26"
                ? "YOLO26 ROI"
                : "Ball ROI";
      drawDetectionRoiBox(ctx, detectionRoi, rect, detLabel);
      const nHit = Array.isArray(samples)
        ? samples.reduce(
            (n, s) => n + (s?.detected && s.source !== "physics" ? 1 : 0),
            0
          )
        : 0;
      if (nHit > 0) {
        const fontSize = Math.max(11, Math.min(w, h) * 0.024);
        ctx.save();
        ctx.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillStyle = BALL_COLOR;
        ctx.textBaseline = "top";
        ctx.fillText(`${nHit} hits`, ox + 8, oy + 8);
        ctx.restore();
      }

      const traj = trajectoryAt(trajectories, playheadMs);
      if (traj?.polyline_norm?.length) {
        drawFittedCurve(ctx, traj.polyline_norm, playheadMs, ox, oy, w, h);
        const bounce = traj.bounce;
        if (bounce && Number.isFinite(Number(bounce.x))) {
          const bx = ox + Number(bounce.x) * w;
          const by = oy + Number(bounce.y) * h;
          ctx.beginPath();
          ctx.strokeStyle = "#4FC3F7";
          ctx.lineWidth = 2;
          ctx.arc(bx, by, Math.max(6, Math.min(w, h) * 0.016), 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      const serveMarks = serveBounceMarksAt(
        serveEvents,
        events,
        playheadMs,
        tableCorners
      );
      const stampR = Math.max(8, Math.min(w, h) * 0.022);
      const stampFont = Math.max(11, Math.min(w, h) * 0.026);
      serveMarks.forEach((mark) => {
        if (!Number.isFinite(mark.x) || !Number.isFinite(mark.y)) return;
        const px = ox + mark.x * w;
        const py = oy + mark.y * h;
        const fill = mark.color || SERVE_BOUNCE_COLOR;
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = fill;
        ctx.globalAlpha = 0.32;
        ctx.arc(px, py, stampR, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.strokeStyle = fill;
        ctx.lineWidth = Math.max(2.2, stampR * 0.22);
        ctx.arc(px, py, stampR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#0b1720";
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = 3;
        ctx.font = `700 ${stampFont}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.strokeText(String(mark.n), px, py + 0.5);
        ctx.fillText(String(mark.n), px, py + 0.5);
        ctx.restore();
      });

      const dots = hitsAroundPlayhead(samples, playheadMs, LAST_DOTS);
      const r = Math.max(3.5, Math.min(w, h) * 0.01);
      dots.forEach((s, i) => {
        const px = ox + Number(s.x) * w;
        const py = oy + Number(s.y) * h;
        const latest = i === dots.length - 1;
        const rr = latest ? r * 1.35 : r;
        ctx.beginPath();
        ctx.fillStyle = DOT_COLOR;
        ctx.arc(px, py, rr, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.strokeStyle = latest ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.35)";
        ctx.lineWidth = latest ? 1.6 : 1.1;
        ctx.arc(px, py, rr, 0, Math.PI * 2);
        ctx.stroke();
      });

      const near = eventsNearPlayhead(events, playheadMs);
      const markerR = Math.max(5, Math.min(w, h) * 0.014);
      const fontPx = Math.max(9, Math.min(w, h) * 0.022);
      near.forEach((ev) => {
        if (
          ev?.type === "TABLE_BOUNCE" &&
          serveMarks.some((m) => Math.abs(m.t_ms - Number(ev.t_ms)) <= 40)
        ) {
          return;
        }
        const style = EVENT_STYLES[ev.type] || {
          color: "#FFFFFF",
          label: "?",
        };
        const px = ox + Number(ev.x) * w;
        const py = oy + Number(ev.y) * h;
        const age = Math.max(0, playheadMs - (ev.t_ms || 0));
        const alpha = Math.max(0.35, 1 - age / EVENT_WINDOW_MS);
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.fillStyle = style.color;
        ctx.arc(px, py, markerR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = 1.25;
        ctx.arc(px, py, markerR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#111";
        ctx.font = `700 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(style.label, px, py + 0.5);
        ctx.globalAlpha = 1;
      });

      const cur = ballSampleAt(samples, playheadMs);
      if (cur?.detected && Number.isFinite(Number(cur.x)) && Number.isFinite(Number(cur.y))) {
        const px = ox + Number(cur.x) * w;
        const py = oy + Number(cur.y) * h;
        const liveR = Math.max(5, Math.min(w, h) * 0.014);
        ctx.beginPath();
        ctx.fillStyle = "rgba(255, 235, 59, 0.95)";
        ctx.arc(px, py, liveR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.lineWidth = 2;
        ctx.arc(px, py, liveR + 3, 0, Math.PI * 2);
        ctx.stroke();
      }
      const spd =
        (cur?.detected && Number(cur.speed_kmh)) ||
        polylineSpeedAt(traj?.polyline_norm, playheadMs) ||
        Number(traj?.speed_peak_kmh);
      if (Number.isFinite(spd) && spd > 0 && cur?.detected) {
        const px = ox + Number(cur.x) * w;
        const py = oy + Number(cur.y) * h;
        const fontSize = Math.max(11, Math.min(w, h) * 0.026);
        ctx.save();
        ctx.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillStyle = BALL_COLOR;
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = 3;
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        const label = `${spd.toFixed(0)} km/h`;
        ctx.strokeText(label, px + r * 2.2, py - r);
        ctx.fillText(label, px + r * 2.2, py - r);
        ctx.restore();
      }
    };

    const video = videoRef?.current;
    if (video) {
      const paintNow = () =>
        paintAt(Math.round((video.currentTime || 0) * 1000));
      paintNow();
      const onSeeked = () => paintNow();
      video.addEventListener("seeked", onSeeked);
      const unsub = subscribePresentedVideoTime(video, (t) => {
        if (video.paused) return;
        paintAt(Math.round(t * 1000));
      });
      const ro = new ResizeObserver(paintNow);
      ro.observe(canvas.parentElement || canvas);
      return () => {
        ro.disconnect();
        video.removeEventListener("seeked", onSeeked);
        unsub();
      };
    }

    paintAt(tMsRef.current);
    const ro = new ResizeObserver(() => paintAt(tMsRef.current));
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [
    samples,
    events,
    trajectories,
    videoRef,
    videoWidth,
    videoHeight,
    visible,
    detectionRoi,
    detector,
    serveEvents,
    tableCorners,
    videoRef ? null : tMs,
  ]);

  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pose-skeleton-overlay ball-track-overlay"
      aria-hidden
    />
  );
}

export default BallTrackOverlay;
